import React, { useEffect, useMemo, useState } from "react";
import {
    CircularProgress,
    Typography,
    Card,
    CardContent,
    Grid,
    Box,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Snackbar,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import LessonCard from "./LessonCard";
import { useAuth } from "@clerk/clerk-react";
import { callBackend, formatBackendError, formatNetworkError } from "../api";
// The twelve languages an operator must pick in Heygen, as a screenshot of Heygen's own
// picker with them circled. Imported from src/ rather than served from public/ so the build
// fingerprints the filename - otherwise an updated screenshot would sit behind a stale cache.
import translationsHint from "../assets/translations-hint.jpg";

function Home() {
    const { getToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [searchField, setSearchField] = useState("lessonId");
    const [searchText, setSearchText] = useState("");
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState("");
    const [selectedStatuses, setSelectedStatuses] = useState({
        completed: false,
        "in-progress": true,
        todo: true,
        notDeleted: false,
        forceDeleted: false,
        cloudflareVideoNotFound: false,
    });
    const [heygenApiKey, setHeygenApiKey] = useState("");
    // Shows the Heygen language-picker screenshot. Session-only on purpose: this is a reminder
    // an operator reaches for, not a setting worth persisting.
    const [languagesHintOpen, setLanguagesHintOpen] = useState(false);
    const [deepSearch, setDeepSearch] = useState(false);
    // Narrows whatever the status filters show to lessons under a Candytrail / TimeSensitive
    // category (the backend's `isNewApp`). It is an AND on top of them, not another status.
    const [onlyNewApp, setOnlyNewApp] = useState(false);
    const [deepSearchCampus, setDeepSearchCampus] = useState("");
    const [campusList, setCampusList] = useState([]);
    const [recaching, setRecaching] = useState({});
    const [recacheDialogOpen, setRecacheDialogOpen] = useState(false);
    const [campusListError, setCampusListError] = useState(null);

    const urlParams = new URLSearchParams(window.location.search);
    const testMode = urlParams.get("testMode") === "true";

    // Defensive: an error payload has none of these keys, and `lessons.map` on
    // undefined used to blank the whole page. `lessons`/`campuses` are normalised
    // inside the useMemo below so they stay stable dependencies.
    const serverStats = (data && data.stats) || {};
    // The backend's counts cover every lesson. With "only new app videos" on, count the
    // new-app subset here instead, so the cards agree with the list underneath them.
    const stats = useMemo(() => {
        if (!onlyNewApp) return serverStats;
        const newApp = (data?.lessons || []).filter((l) => l.isNewApp === true);
        return {
            total: newApp.length,
            completed: newApp.filter((l) => l.status === "completed").length,
            inProgress: newApp.filter((l) => l.status === "in-progress").length,
            todo: newApp.filter((l) => l.status === "todo").length,
        };
    }, [onlyNewApp, data]); // eslint-disable-line react-hooks/exhaustive-deps

    // The `/admin/trigger-*` routes answer HTTP 200 with `{ success: false, message }`
    // when a recache is already running - a real answer with no `error` key, so
    // `callBackend` reports it as ok. Surface the backend's own wording instead of a
    // fake success.
    const cacheTriggerMessage = (result, fallback) => {
        const payload = result.data || {};

        if (payload.success === false) {
            return payload.message || "The backend declined the request.";
        }

        return payload.message || fallback;
    };

    // Function to trigger deep search recache for a specific campus
    const triggerDeepSearchRecache = async (campusId) => {
        setRecaching(prev => ({ ...prev, [campusId]: true }));
        try {
            const result = await callBackend(`/admin/trigger-deep-search/${campusId}`, {
                token: await getToken(),
            });

            if (result.ok) {
                setMessage(cacheTriggerMessage(result, `Deep search cache refreshed for ${campusId}.`));
            } else {
                setMessage(formatBackendError(result, `Deep search recache (${campusId}) failed`));
            }
            setOpen(true);
        } catch (err) {
            setMessage(formatNetworkError(err, `Deep search recache (${campusId}) failed`));
            setOpen(true);
        }
        setRecaching(prev => ({ ...prev, [campusId]: false }));
    };

    // Function to trigger regular fetch cache
    const triggerRegularFetchCache = async () => {
        setRecaching(prev => ({ ...prev, regular: true }));
        try {
            const result = await callBackend("/admin/trigger-regular-fetch", {
                token: await getToken(),
            });

            if (result.ok) {
                setMessage(cacheTriggerMessage(result, "Regular fetch cache refreshed."));
            } else {
                setMessage(formatBackendError(result, "Regular fetch cache failed"));
            }
            setOpen(true);
        } catch (err) {
            setMessage(formatNetworkError(err, "Regular fetch cache failed"));
            setOpen(true);
        }
        setRecaching(prev => ({ ...prev, regular: false }));
    };

    // Function to trigger all caching (regular + deep search for all campuses)
    const triggerAllCaching = async () => {
        setRecacheDialogOpen(false);
        try {
            const token = await getToken();

            // The route is fire-and-forget: it returns as soon as the ~10 minute crawl
            // is queued, so this response only ever means "started", never "finished".
            const result = await callBackend("/admin/trigger-deep-search", { token });

            if (result.ok) {
                setMessage(cacheTriggerMessage(result, "Recaching started. Watch the pod logs for progress."));
            } else {
                setMessage(formatBackendError(result, "Failed to start recaching"));
            }
            setOpen(true);
        } catch (err) {
            console.error("Failed to trigger recaching:", err);
            setMessage(formatNetworkError(err, "Failed to trigger recaching"));
            setOpen(true);
        }
    };

    // A silent refresh must not destroy what is on screen: the list stays, and the
    // failure is reported through the snackbar so the outcome of the action that
    // triggered the refresh is still visible next to it.
    const reportFetchFailure = (silent, failure) => {
        if (silent) {
            setMessage(`Background refresh failed - the list below may be out of date, but the action that triggered it is unaffected: ${failure}`);
            setOpen(true);
            return;
        }

        setError(failure);
        setData(null);
    };

    // Function to fetch lessons (separated from useEffect to avoid infinite loops)
    // `silent` refreshes the list in place, so the outcome snackbar of whatever
    // triggered the refresh is not replaced by the full-page spinner.
    const fetchLessons = async (options = {}) => {
        const silent = options.silent === true;
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        try {
            const token = await getToken();

            // Build query parameters. The backend rejects `deepSearch=true` without a
            // campus, so the flag only goes out once a campus is picked - otherwise the
            // regular list (which is what is on screen) is refreshed instead.
            const params = new URLSearchParams();
            if (deepSearch && deepSearchCampus) {
                params.append('deepSearch', 'true');
                params.append('deepSearchCampus', deepSearchCampus);
            }

            const path = `/fetch${params.toString() ? `?${params.toString()}` : ''}`;

            const result = await callBackend(path, { token });

            // `{ error: "..." }` arrives with HTTP 200, so both cases land here.
            if (!result.ok) {
                reportFetchFailure(silent, formatBackendError(result, "Failed to load lessons"));
                return;
            }

            setData(result.data);
            setError(null);
        } catch (err) {
            reportFetchFailure(silent, formatNetworkError(err, "Failed to load lessons"));
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    // Initial data load - only runs once when component mounts
    useEffect(() => {
        if (!data && !loading) {
            fetchLessons();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getToken]);

    // Poll while any lesson is waiting on Cloudflare.
    //
    // The backend now finishes lessons on its own: a background sweep verifies the Cloudflare
    // copies, publishes to TRW and deletes the Drive source without anyone clicking. Nothing in
    // this app ever polled, because until now the server only changed while the operator was
    // pressing a button - so a lesson would sit on "Cloudflare is encoding" forever even after it
    // had actually published. `silent` refreshes in place and leaves the snackbar alone.
    //
    // It stops as soon as nothing is pending, so an idle Home makes no requests.
    const pendingCloudflareCount = (data?.lessons || []).filter(
        (l) => (l.cloudflarePending || []).length > 0 || l.cloudflareBlockedAt || l.preparingSource
    ).length;

    useEffect(() => {
        if (pendingCloudflareCount === 0) return undefined;
        const timer = setInterval(() => fetchLessons({ silent: true }), 30000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingCloudflareCount, deepSearch, deepSearchCampus]);

    // Re-fetch when deep search campus is selected (but not on initial load)
    useEffect(() => {
        // Only re-fetch once the initial load has settled (data or error), deep search is
        // enabled, AND a campus is selected - picking a campus has to work from the error
        // banner too, otherwise Retry is the only way out.
        if ((data || error) && deepSearch && deepSearchCampus) {
            fetchLessons();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepSearchCampus, deepSearch]);

    useEffect(() => {
        const fetchCampuses = async () => {
            try {
                const token = await getToken();
                const result = await callBackend("/campuses", { token });

                if (result.ok) {
                    setCampusList((result.data && result.data.campuses) || []);
                    setCampusListError(null);
                } else {
                    setCampusList([]);
                    setCampusListError(formatBackendError(result, "Failed to load campuses"));
                }
            } catch (err) {
                console.error("Failed to fetch campuses:", err);
                setCampusList([]);
                setCampusListError(formatNetworkError(err, "Failed to load campuses"));
            }
        };

        fetchCampuses();
    }, [getToken]);

    const handleFilterChange = (event) => {
        const { name, checked } = event.target;
        setSelectedStatuses((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleOpen = (message) => {
        setMessage(message);
        setOpen(true);
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpen(false);
    };

    // Combine lesson data with campus names
    const lessonsWithCampus = useMemo(() => {
        const lessons = Array.isArray(data?.lessons) ? data.lessons : [];
        const campuses = (data && data.campuses) || {};

        return lessons.map((lesson) => ({
            ...lesson,
            campusName: campuses[lesson.campusId] || "Unknown Campus",
        }));
    }, [data]);

    const getColor = (key) => {
        switch (key) {
            case "completed":
                return "success.main";
            case "inProgress":
                return "warning.main";
            case "todo":
                return "info.main";
            case "total":
                return "text.primary";
            default:
                return "text.primary";
        }
    };

    return (
        <Box sx={{ maxWidth: 500, margin: "auto", padding: 2 }}>
            {/* Banners, not early returns: the deep search checkbox, the filters and the
                snackbar have to stay mounted so a failed load is always recoverable. */}
            {error && (
                <Box sx={{ mb: 2, p: 2, border: 1, borderColor: "error.main", borderRadius: 1 }}>
                    <Typography color="error" sx={{ wordBreak: "break-word" }}>{error}</Typography>
                    <Button variant="contained" size="small" sx={{ mt: 2 }} onClick={() => fetchLessons()}>
                        Retry
                    </Button>
                </Box>
            )}

            {loading && (
                <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                </Box>
            )}

            <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={() => setRecacheDialogOpen(true)}
                >
                    Trigger Recaching
                </Button>
            </Box>

            <Grid container spacing={2} textAlign="center">
                {[
                    ["total", "Total"],
                    ["completed", "Completed"],
                    ["inProgress", "In Progress"],
                    ["todo", "Pending"],
                ].map((key) => (
                    <Grid item xs={6} key={key[0]}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6">{key[1]}</Typography>
                                <Typography variant="h4" color={getColor(key[0])}>
                                    {stats[key[0]] || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Box mt={2} display="flex" gap={2} alignItems="center">
                <TextField
                    select
                    label="Search by"
                    value={searchField}
                    onChange={(e) => setSearchField(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ width: 150 }}
                    SelectProps={{ native: true }}
                >
                    <option value="lessonId">Lesson ID</option>
                    <option value="lesson">Lesson Title</option>
                    <option value="module">Module</option>
                    <option value="category">Category</option>
                    <option value="campusName">Campus</option>
                    <option value="googleDriveLink">Link</option>
                    <option value="videoId">VideoId</option>
                </TextField>

                <TextField
                    label="Search"
                    variant="outlined"
                    size="small"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    fullWidth
                />
            </Box>

            <FormGroup row sx={{ mb: 2 }}>
                {[
                    ["todo", "Pending"],
                    ["in-progress", "In Progress"],
                    ["completed", "Completed"],
                    ["notDeleted", "Completed but not Deleted"],
                    ["forceDeleted", "Force Deleted"],
                    ["cloudflareVideoNotFound", "Cloudflare Video Not Found"],
                ].map((status) => (
                    <FormControlLabel
                        key={status}
                        control={
                            <Checkbox
                                checked={selectedStatuses[status[0]]}
                                onChange={handleFilterChange}
                                name={status[0]}
                            />
                        }
                        label={status[1]}
                    />
                ))}
            </FormGroup>

            <FormGroup row sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={onlyNewApp}
                            onChange={(e) => setOnlyNewApp(e.target.checked)}
                            name="onlyNewApp"
                        />
                    }
                    label={`Only show new app videos (${serverStats.newApp ?? 0})`}
                />
                {/* A list cached before 1.0.61 has no `isNewApp` on any lesson, which would read as
                    "no new app videos" rather than "not known yet". Say which it is. */}
                {onlyNewApp && (data?.lessons || []).length > 0 && !(data?.lessons || []).some((l) => "isNewApp" in l) && (
                    <Typography variant="body2" color="warning.main" sx={{ alignSelf: "center" }}>
                        This lesson list was cached before the new-app filter existed. Trigger recaching to populate it.
                    </Typography>
                )}
            </FormGroup>

            <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                    Deep Search
                </Typography>

                {campusListError && (
                    <Typography variant="body2" color="error.main" sx={{ mb: 1, wordBreak: "break-word" }}>
                        {campusListError}
                    </Typography>
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={deepSearch}
                            onChange={(e) => setDeepSearch(e.target.checked)}
                            name="deepSearch"
                        />
                    }
                    label="Enable Deep Search"
                />
                
                {deepSearch && (
                    <TextField
                        select
                        label="Select Campus"
                        value={deepSearchCampus}
                        onChange={(e) => setDeepSearchCampus(e.target.value)}
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ mt: 1 }}
                        SelectProps={{ native: true }}
                    >
                        <option value="">All Campuses</option>
                        {campusList.map((campus) => (
                            <option key={campus.campusId} value={campus.campusId}>
                                {campus.name}
                            </option>
                        ))}
                    </TextField>
                )}

                {testMode && deepSearch && (
                    <Box sx={{ mt: 1 }}>
                        {campusList.map((campus) => (
                            <Button
                                key={campus.campusId}
                                variant="contained"
                                color="secondary"
                                onClick={() => triggerDeepSearchRecache(campus.campusId)}
                                disabled={recaching[campus.campusId]}
                                sx={{ mr: 1, mb: 1 }}
                            >
                                {recaching[campus.campusId] ? "Caching..." : `Cache ${campus.name}`}
                            </Button>
                        ))}
                    </Box>
                )}

                {testMode && (
                    <Box sx={{ mt: 1 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={triggerRegularFetchCache}
                            disabled={recaching.regular}
                            sx={{ mr: 1, mb: 1 }}
                        >
                            {recaching.regular ? "Caching..." : "Cache Regular Fetch"}
                        </Button>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 2, mb: 2 }}>
                <TextField
                    label="Heygen API Key"
                    variant="outlined"
                    size="small"
                    type="password"
                    value={heygenApiKey}
                    onChange={(e) => setHeygenApiKey(e.target.value)}
                    fullWidth
                />
                <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<HelpOutlineIcon />}
                    onClick={() => setLanguagesHintOpen(true)}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0, height: 40 }}
                >
                    Which languages?
                </Button>
            </Box>

            {/* Reminder of exactly which twelve languages to select in Heygen. The names must match
                HEYGEN_LANGUAGE_NAMES on the backend byte-for-byte - a near-miss (there are nine
                Chinese variants) maps to nothing and leaves that language stuck in-progress. */}
            <Dialog
                open={languagesHintOpen}
                onClose={() => setLanguagesHintOpen(false)}
                maxWidth="lg"
                fullWidth
                aria-labelledby="languages-hint-title"
            >
                <DialogTitle id="languages-hint-title" sx={{ pr: 6 }}>
                    Select these 12 languages in Heygen
                    <IconButton
                        aria-label="Close"
                        onClick={() => setLanguagesHintOpen(false)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box
                        component="img"
                        src={translationsHint}
                        alt="Heygen's language picker with the twelve required languages circled: Spanish, Portuguese, Russian, Arabic, Turkish, Romanian, Chinese (Mandarin, Simplified), Japanese, German, Korean, Polish, French - and the Precision engine circled."
                        sx={{ width: "100%", height: "auto", display: "block", borderRadius: 1 }}
                    />
                    <DialogContentText sx={{ mt: 2 }}>
                        Pick the names exactly as shown. <strong>Chinese (Mandarin, Simplified)</strong> is the one
                        to be careful with - Heygen offers several near-identical Chinese options and only this
                        one is recognised; anything else leaves that language unfinished. Heygen caps a single
                        job at 10 languages, so submit them in more than one batch and record every folder ID.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLanguagesHintOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Box mt={3}>
                {lessonsWithCampus
                    .filter((lesson) => {
                        if (onlyNewApp && lesson.isNewApp !== true) return false;

                        const matchCompleted = selectedStatuses.completed && lesson.status === "completed";
                        const matchInProgress = selectedStatuses["in-progress"] && lesson.status === "in-progress" && !lesson.isForceDeleted;
                        const matchTodo = selectedStatuses.todo && lesson.status === "todo";
                        const matchCompletedNotDeleted = selectedStatuses.notDeleted && lesson.status === "completed" && lesson.isDeleted === false;
                        const matchForceDeleted = selectedStatuses.forceDeleted && lesson.isForceDeleted === true;
                        const matchCloudflareVideoNotFound = selectedStatuses.cloudflareVideoNotFound && lesson.isCloudflareVideoNotFound === true;

                        return matchCompleted || matchInProgress || matchTodo || matchCompletedNotDeleted || matchForceDeleted || matchCloudflareVideoNotFound;
                    })
                    .map((lesson) => (
                        <LessonCard
                            key={lesson.lessonId}
                            lesson={lesson}
                            searchField={searchField}
                            searchText={searchText}
                            googleDriveLink={lesson.googleDriveLink}
                            handleOpen={handleOpen}
                            heygenApiKey={heygenApiKey}
                            onLessonChanged={() => fetchLessons({ silent: true })}
                        />
                    ))}
            </Box>

            <Snackbar
                open={open}
                autoHideDuration={6000}
                onClose={handleClose}
                message={message}
            />

            <Dialog open={recacheDialogOpen} onClose={() => setRecacheDialogOpen(false)}>
                <DialogTitle>Trigger Recaching</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will refresh all application caches. It takes approximately 10 minutes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRecacheDialogOpen(false)}>Cancel</Button>
                    <Button onClick={triggerAllCaching} variant="contained" color="warning">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Home;
